import { useEffect, useState } from 'react'

import { router } from '@inertiajs/react'
import { PrimaryNavbar } from 'shared/Navbar'
import { RoleSelect } from 'shared/Profile/RoleSelect'
import { colors } from 'theme'
import { makeStyles } from 'tss-react/mui'

import { useTabStyles } from 'shared/Navbar/hooks/useTabStyles'

import { type User } from 'helpers/interfaces'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'

import { faBars, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const MENU_ID = 'mobile-menu'

// The navigation stacked and centred, as in the legacy page's mobile menu
const useStyles = makeStyles()((theme) => ({
  list: {
    flexDirection: 'column',
    padding: theme.spacing(2, 0),
  },
  listItem: {
    minHeight: 56,
  },
  button: {
    border: 0,
    padding: 0,
    background: 'none',
    font: 'inherit',
    cursor: 'pointer',
  },
}))

// Below the sm breakpoint the header's navigation and profile don't fit, so they move into a menu
export const PageMobileMenu = ({ user }: { user: User }) => {
  const [open, setOpen] = useState(false)
  const { classes, cx } = useStyles()
  const { classes: tabClasses } = useTabStyles()

  useEffect(() => {
    if (!open) return

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [open])

  return (
    // Tapping anywhere else closes it. Contents display, so the menu still sits under the whole header
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ display: 'contents' }}>
        <Button
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-expanded={open}
          aria-controls={MENU_ID}
          // Large blue icons, as on the legacy page
          endIcon={
            <FontAwesomeIcon icon={open ? faXmark : faBars} color={colors.infoMain} style={{ height: 24, width: 24 }} />
          }
          sx={{ fontSize: 18, fontWeight: 400, color: 'text.primary' }}
        >
          {open ? 'Close' : 'Menu'}
        </Button>
        <Collapse
          in={open}
          id={MENU_ID}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            bgcolor: 'background.paper',
            boxShadow: 3,
            // Scrolls within the screen when the links don't fit
            '& .MuiCollapse-wrapperInner': { maxHeight: '70vh', overflowY: 'auto' },
          }}
        >
          <PrimaryNavbar user={user} classes={{ list: classes.list, listItem: classes.listItem }} />
          <Divider variant="middle" />
          <ul className={cx(tabClasses.list, classes.list)} aria-label="Account">
            {/* The profile popover's role switch, which has no room on phones */}
            {user.roles.length > 1 ? (
              <li className={cx(tabClasses.listItem, classes.listItem)}>
                <Box sx={{ width: 200 }}>
                  <RoleSelect user={user} />
                </Box>
              </li>
            ) : null}
            <li className={cx(tabClasses.listItem, classes.listItem)}>
              <a href="/account" className={tabClasses.link}>
                Profile
              </a>
            </li>
            <li className={cx(tabClasses.listItem, classes.listItem)}>
              <button
                type="button"
                className={cx(tabClasses.link, classes.button)}
                onClick={() => router.delete('/users/sign_out')}
              >
                Logout
              </button>
            </li>
          </ul>
        </Collapse>
      </Box>
    </ClickAwayListener>
  )
}
